import { signInWithCustomToken, type Auth } from 'firebase/auth';

import {
  installImpersonationClaimWatcher,
  markImpersonating,
  markNormal,
} from './impersonation';

/**
 * Boot-time consumer for `?becomeToken=<custom token>` — the admin
 * impersonation link minted by thea-serverless `scripts/become_user.py`
 * (Admin-SDK-only minting; there is deliberately no callable).
 *
 * Same discipline as the claim route's `?g=`: the token is a BEARER
 * CREDENTIAL (possession signs you in as the target user), so it is captured
 * synchronously and stripped from the URL before the first await — never left
 * in history or a copied link. `sensitiveUrlParams.ts` scrubs it from any
 * analytics hit that fires before this runs.
 *
 * Single-flight with two callers, one promise:
 *  - `ensureAuth()` (firebaseConfig.js) awaits it FIRST, so the custom-token
 *    sign-in can never lose a race against the anon fallback mint or a
 *    persisted-session restore;
 *  - `BecomeUserMount` (App.js) calls it too, covering routes where nothing
 *    happens to call ensureAuth.
 *
 * Failure (the token expires 1 hour after minting) logs a console.warn and
 * falls through to a normal boot — a dead link must not strand the page.
 */

export const BECOME_PARAM = 'becomeToken';

let bootPromise: Promise<void> | null = null;

function readAndStripBecomeToken(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const token = params.get(BECOME_PARAM);
    if (!token) return null;
    params.delete(BECOME_PARAM);
    const qs = params.toString();
    const next = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
    window.history.replaceState(window.history.state, '', next);
    return token;
  } catch {
    return null; // Sandboxed embed etc. — boot proceeds as a normal session.
  }
}

async function consume(auth: Auth): Promise<void> {
  if (typeof window === 'undefined') return;
  // Truth source for banner + telemetry gates, every boot (no-op repeat-safe).
  installImpersonationClaimWatcher(auth);
  const token = readAndStripBecomeToken();
  if (!token) return;
  // Provisional, BEFORE the sign-in resolves: the session's first GA/sink
  // events must already be gated while the custom-token sign-in is in flight.
  markImpersonating();
  try {
    await signInWithCustomToken(auth, token);
    // The claim watcher confirms and fills in the impersonator label.
  } catch (err) {
    markNormal();
    // eslint-disable-next-line no-console
    console.warn(
      '[becomeUser] impersonation sign-in failed (links expire 1h after minting):',
      err,
    );
  }
}

/** Idempotent boot hook — see the module note for the two call sites. */
export function consumeBecomeTokenOnce(auth: Auth): Promise<void> {
  if (!bootPromise) bootPromise = consume(auth);
  return bootPromise;
}

/** Test-only. */
export function _resetBecomeUserForTesting(): void {
  bootPromise = null;
}
